/**
 * Loading-state placeholder for UdeDataView.
 *
 * Mirrors the layout that the configured query will eventually render
 * (chart shape + axis ticks + legend on right or bottom) using shimmering
 * skeleton blocks. The goal is a calm, structural preview rather than an
 * obtrusive spinner.
 */
import React, { useMemo } from 'react';
import styled, { keyframes } from 'styled-components';
import { Box, Flex } from 'core/components';
import { ProductSkeleton } from 'core/components/Skeletons';
import QueryModel from 'app/stores/ude/QueryModel';

interface UdeDataViewSkeletonProps {
  queryModel: QueryModel;
  legendMaxWidth?: number | string;
  padding?: number;
}

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const Container = styled(Flex)`
  animation: ${fadeIn} 0.2s ease-out both;
`;

const ChartArea = styled(Flex)`
  flex: 1;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
`;

const PlotBox = styled(Flex)`
  flex: 1;
  min-height: 0;
  position: relative;
  align-items: stretch;
`;

const GridLines = styled.div`
  position: absolute;
  inset: 0;
  background-image: linear-gradient(to bottom, rgba(0, 0, 0, 0.04) 1px, transparent 1px);
  background-size: 100% 25%;
  pointer-events: none;
`;

const RIGHT_LEGEND_DEFAULT_MAX_WIDTH = '40%';

const TICK_COUNT = 5;
const DEFAULT_LEGEND_ROWS = 5;

/** Pseudo-random but stable widths so the skeleton looks varied yet doesn't reflow. */
function pickWidth(seed: number, min: number, max: number): string {
  // Deterministic hash → [0, 1)
  const x = Math.sin(seed * 9999) * 10000;
  const r = x - Math.floor(x);
  return `${Math.round(min + r * (max - min))}%`;
}

interface YAxisTicksProps {
  count: number;
}
const YAxisTicks = ({ count }: YAxisTicksProps) => (
  <Flex flexDirection="column" justifyContent="space-between" pr="8px" py="6px" style={{ width: 44 }}>
    {Array.from({ length: count }, (_, i) => (
      <ProductSkeleton key={i} height="8px" width="100%" />
    ))}
  </Flex>
);

interface XAxisTicksProps {
  count: number;
  leftOffset?: number;
}
const XAxisTicks = ({ count, leftOffset = 0 }: XAxisTicksProps) => (
  <Flex justifyContent="space-between" pt="6px" px="6px" style={{ marginLeft: leftOffset }}>
    {Array.from({ length: count }, (_, i) => (
      <ProductSkeleton key={i} height="8px" width="40px" />
    ))}
  </Flex>
);

/** Vertical-bar style plot body (bar / stackedBar). */
const BarPlot = ({ count = 12 }: { count?: number }) => (
  <Flex flex={1} alignItems="flex-end" justifyContent="space-around" px="8px" pb="4px" style={{ gap: 6 }}>
    {Array.from({ length: count }, (_, i) => (
      <ProductSkeleton key={i} height={pickWidth(i + 1, 30, 95)} width="100%" borderRadius="4px 4px 0 0" />
    ))}
  </Flex>
);

/** Circular plot body (pie / sunburst). */
const CirclePlot = () => (
  <Flex flex={1} alignItems="center" justifyContent="center">
    <ProductSkeleton height="220px" width="220px" borderRadius="50%" />
  </Flex>
);

/** Generic full-bleed plot body (line / area / matrix / etc). */
const RectPlot = () => (
  <Box flex={1} m="6px">
    <ProductSkeleton height="100%" width="100%" borderRadius="6px" />
  </Box>
);

/** Table-style placeholder rows (used for table viz type). */
const TableSkeleton = ({ rows, cols }: { rows: number; cols: number }) => (
  <Flex flexDirection="column" flex={1} p="8px" style={{ gap: 8 }}>
    <Flex style={{ gap: 12 }}>
      {Array.from({ length: cols }, (_, c) => (
        <ProductSkeleton key={c} height="10px" width={`${Math.floor(100 / cols)}%`} />
      ))}
    </Flex>
    {Array.from({ length: rows }, (_, r) => (
      <Flex key={r} style={{ gap: 12 }}>
        {Array.from({ length: cols }, (__, c) => (
          <ProductSkeleton key={c} height="14px" width={pickWidth(r * cols + c, 50, 95)} />
        ))}
      </Flex>
    ))}
  </Flex>
);

interface LegendSkeletonProps {
  rowCount: number;
  valueCount: number;
  position: 'right' | 'bottom' | 'left';
  maxWidth?: number | string;
}

const LegendSkeleton = ({ rowCount, valueCount, position, maxWidth }: LegendSkeletonProps) => {
  const isRight = position === 'right' || position === 'left';

  return (
    <Flex
      flexDirection={isRight ? 'column' : 'row'}
      flexWrap={isRight ? 'nowrap' : 'wrap'}
      p={1}
      style={{
        gap: 16,
        ...(isRight
          ? {
              maxWidth: maxWidth ?? RIGHT_LEGEND_DEFAULT_MAX_WIDTH,
              minWidth: 310,
              overflow: 'hidden'
            }
          : { width: '100%', justifyContent: 'center' })
      }}
    >
      {Array.from({ length: rowCount }, (_, i) => (
        <Flex key={i} alignItems="center" style={{ gap: 8, minWidth: 0 }}>
          <ProductSkeleton height="10px" width={pickWidth(i + 7, 45, 90)} />
          {isRight && valueCount > 0 && (
            <Flex style={{ gap: 6, marginLeft: 'auto', flex: 'none' }}>
              {Array.from({ length: valueCount }, (__, v) => (
                <ProductSkeleton key={v} height="10px" width="36px" />
              ))}
            </Flex>
          )}
        </Flex>
      ))}
    </Flex>
  );
};

function UdeDataViewSkeleton({ queryModel, legendMaxWidth, padding }: UdeDataViewSkeletonProps) {
  const chartType = queryModel.settings?.chart?.type ?? 'line';
  const isTableOnly = chartType === 'table';
  const legendEnabled = (isTableOnly || queryModel.settings?.chart?.legend?.enable) ?? true;
  const legendPosition: 'bottom' | 'right' | 'left' = isTableOnly
    ? 'bottom'
    : ((queryModel.settings?.chart?.legend?.position as 'bottom' | 'right' | 'left' | undefined) ?? 'right');

  const { rowCount, valueCount } = useMemo(() => {
    const { query } = queryModel;
    const rollups = query.rollups?.length ?? 0;
    const metrics = query.metrics?.length ?? 0;
    const dimensions = query.dimensions?.length ?? 0;
    return {
      rowCount: dimensions > 0 ? DEFAULT_LEGEND_ROWS : Math.max(metrics, 1),
      valueCount: rollups > 0 ? rollups : Math.max(metrics, 1)
    };
  }, [queryModel]);

  const renderPlot = () => {
    if (isTableOnly) {
      return <TableSkeleton rows={6} cols={Math.max(valueCount + 1, 3)} />;
    }
    if (chartType === 'pie' || chartType === 'sunburst') {
      return <CirclePlot />;
    }
    if (chartType === 'bar' || chartType === 'stackedBar') {
      return <BarPlot />;
    }
    return <RectPlot />;
  };

  const showAxes = !isTableOnly && chartType !== 'pie' && chartType !== 'sunburst';

  const chartSection = !isTableOnly && queryModel.settings?.chart?.enable !== false && (
    <ChartArea p={padding ?? 1}>
      <PlotBox>
        {showAxes && <YAxisTicks count={TICK_COUNT} />}
        <Flex flex={1} flexDirection="column" minWidth={0}>
          <PlotBox>
            {showAxes && <GridLines />}
            {renderPlot()}
          </PlotBox>
          {showAxes && <XAxisTicks count={TICK_COUNT} />}
        </Flex>
      </PlotBox>
    </ChartArea>
  );

  const directionMap = { right: 'row', left: 'row-reverse', bottom: 'column' } as const;
  const flexDir = directionMap[legendPosition];

  return (
    <Container
      flexDirection={flexDir}
      width="100%"
      height="100%"
      overflow="hidden"
      alignItems="stretch"
      role="status"
      aria-label="Loading query results"
    >
      {chartSection}
      {isTableOnly && (
        <Flex flex={1} minWidth={0} minHeight={0} p={padding ?? 1}>
          <TableSkeleton rows={6} cols={Math.max(valueCount + 1, 3)} />
        </Flex>
      )}
      {legendEnabled && !isTableOnly && (
        <LegendSkeleton
          rowCount={rowCount}
          valueCount={valueCount}
          position={legendPosition}
          maxWidth={legendMaxWidth}
        />
      )}
    </Container>
  );
}

export default UdeDataViewSkeleton;
